import { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, User } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth, formatApiError } from "@/lib/auth";

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    full_name: "",
    email: searchParams.get("email") || "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Please use a password of at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await signup(form);
      toast.success("Welcome! Your profile is ready.");
      navigate(location.state?.from || "/me", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="max-w-md mx-auto px-6 pt-12 md:pt-20 pb-24" data-testid="signup-page">
        <span className="label-overline">Your private space</span>
        <h1 className="font-serif text-5xl text-oxblood mt-3">Create your profile.</h1>
        <p className="text-inkSoft mt-3">
          Track the goals we discussed, see meeting notes, and book follow-ups in one click.
        </p>
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="signup-form">
          <div>
            <label className="label-overline flex items-center gap-2"><User size={12} /> Full name</label>
            <input type="text" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input-flat mt-2" data-testid="signup-name" />
          </div>
          <div>
            <label className="label-overline flex items-center gap-2"><Mail size={12} /> Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-flat mt-2" data-testid="signup-email" />
          </div>
          <div>
            <label className="label-overline flex items-center gap-2"><Lock size={12} /> Password (at least 8 characters)</label>
            <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-flat mt-2" data-testid="signup-password" />
          </div>
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="signup-submit">
            {submitting ? "Creating your profile…" : "Create my profile"}
          </button>
        </form>
        <p className="mt-8 text-sm text-inkSoft">
          Already have an account? <Link to="/login" className="text-cognac link-underline" data-testid="signup-to-login">Sign in</Link>
        </p>
      </main>
      <Footer />
    </div>
  );
}
